import React, { useCallback, useState } from "react";
import { Box, Typography, Link, Button, CircularProgress } from "@mui/material";
import { Download } from "lucide-react";
import type { AssistantArtifactPart } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";
import { renderDocHtml, type DocIR } from "./docRenderer";
import { useArtifactExport, type ArtifactExportFormat } from "./useArtifactExport";

function sanitizeFilename(title: string): string {
  return title.replace(/[<>:"/\\|?*]+/g, "_").replace(/\s+/g, " ").trim() || "report";
}

/** The formats the export route renders for a document, and what each is called
 *  on each surface.
 *
 *  `html` is labelled "View" outside the embed because a plain navigation opens
 *  it in a tab, and "HTML" inside, where it arrives as a saved file instead: a
 *  blob URL cannot be opened in a new tab reliably, since `window.open` after an
 *  awaited fetch has lost its user-gesture and is what popup blockers exist to
 *  stop. Calling it "View" there would promise something it does not do -- and
 *  the document is already rendered in the card's iframe anyway.
 *
 *  `pdf` is absent on purpose, on both surfaces: the PDF button above is
 *  html2pdf.js rendering the same IR client-side, and offering two PDFs that
 *  differ (raster vs the server's vector) would be a puzzle, not a choice. */
const SERVER_FORMATS = [
  { fmt: "html", linkLabel: "View", embedLabel: "HTML", ph: "export_html" },
  { fmt: "docx", linkLabel: "DOCX", embedLabel: "DOCX", ph: "export_docx" },
  { fmt: "pptx", linkLabel: "PPTX", embedLabel: "PPTX", ph: "export_pptx" },
  { fmt: "xlsx", linkLabel: "XLSX", embedLabel: "XLSX", ph: "export_xlsx" },
] as const satisfies readonly { fmt: ArtifactExportFormat; linkLabel: string; embedLabel: string; ph: string }[];

export function ArtifactDocCard({ part }: { part: AssistantArtifactPart }) {
  const [downloading, setDownloading] = useState(false);
  const { isEmbed, busy, error, hrefFor, runEmbedExport } = useArtifactExport(
    part.artifact_id,
    part.title,
  );

  let html = "";
  try {
    const ir = JSON.parse(part.text) as DocIR;
    html = renderDocHtml(ir);
  } catch {
    html = "<!doctype html><body style='font-family:sans-serif;padding:16px'>Preparing document…</body>";
  }

  const handleDownloadPdf = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);
      try {
        await html2pdf()
          .set({
            margin: [10, 10, 10, 10],
            filename: `${sanitizeFilename(part.title ?? "report")}.pdf`,
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .from(container)
          .save();
      } finally {
        document.body.removeChild(container);
      }
    } finally {
      setDownloading(false);
    }
  }, [html, part.title, downloading]);

  return (
    <AnimatedCard variant="sm" sx={{ my: 1, p: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {part.title && <Typography variant="caption" sx={{ flex: 1 }}>{part.title}</Typography>}
        {part.status === "streaming" && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>streaming…</Typography>
        )}
        {part.status === "complete" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              data-ph="download_pdf"
              onClick={handleDownloadPdf}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={14} /> : <Download size={14} />}
              sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25, px: 1 }}
            >
              Download PDF
            </Button>
            {/* Offered on both surfaces, by different means. Outside the embed a
                plain link: same origin, session cookie, and the browser names
                the file from Content-Disposition. Inside it there is no cookie
                and the origin differs, so a relative href would resolve against
                the HOST page and arrive unauthenticated -- the bytes are fetched
                with the bearer instead. Hiding these in the embed, as this did,
                hid DOCX/PPTX/XLSX from every EOLAS-V1 user, since V1 mounts
                Artie as a pure iframe. */}
            {SERVER_FORMATS.map((format) =>
              isEmbed ? (
                <Button
                  key={format.fmt}
                  data-ph={format.ph}
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => void runEmbedExport(format.fmt)}
                  sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: 0, py: 0.25, px: 0.5 }}
                >
                  {format.embedLabel}
                </Button>
              ) : (
                <Link
                  key={format.fmt}
                  data-ph={format.ph}
                  href={hrefFor(format.fmt)}
                  target="_blank" rel="noopener noreferrer"
                  variant="caption"
                >
                  {format.linkLabel}
                </Link>
              ),
            )}
          </Box>
        )}
      </Box>
      {error && (
        // In the card, not a toast: the failure belongs next to the document it
        // was about, and the embed mounts no notifications provider.
        <Typography
          role="alert"
          variant="caption"
          sx={{ display: "block", mb: 0.5, color: "error.main" }}
        >
          {error}
        </Typography>
      )}
      <iframe
        sandbox=""
        srcDoc={html}
        style={{ width: "100%", height: 420, border: 0, background: "white" }}
        title={part.title ?? "document artifact"}
      />
    </AnimatedCard>
  );
}
